import { Button, Image, Spin } from 'antd';
import { FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react';

import { Schemas } from '@api-client/generated/types';
import { IconDownload, IconEyeOff } from '@assets';
import { FileViewer } from '@entities';
import {
  downloadFile,
  getFilePreviewKind,
  isPreviewPending,
  isPreviewReady,
  isRenderableImageMimeType,
  resolvePreviewSrc,
} from '@utils';

import * as S from './styled';

type File = Schemas.Document;
type TaskFile = Schemas.TaskFile;
type CompanyFile = Schemas.CompanyFile;
type PayslipDocument = Schemas.PayslipDocument;
type BillingDocument = Schemas.BillingDocument;
type ObligationFile = Schemas.AdminObligationFileResponseDto;

export type FilePreviewSize = {
  width?: string;
  height?: string;
};

type FilePreviewProps = {
  title?: string;
  headerContent?: ReactNode;
  footerContent?: ReactNode;
  // 'a4' renders PDFs and images in an identical, height-anchored A4 frame with
  // a shared Zoom/Download panel. 'viewer' renders images through the same
  // FileViewer as PDFs (centered, with the standard Zoom/Download panel) instead
  // of an inline click-to-zoom Image. Defaults to the legacy per-consumer layout
  // so other screens are unaffected.
  variant?: 'default' | 'a4' | 'viewer';
  file:
    | File
    | TaskFile
    | CompanyFile
    | PayslipDocument
    | BillingDocument
    | ObligationFile
    | null;
} & FilePreviewSize;

const FilePreviewWrapper: FC<
  PropsWithChildren<Omit<FilePreviewProps, 'file'>>
> = ({ title, headerContent, footerContent, children }) => (
  <S.Preview>
    {title && <S.PreviewTitle>{title}</S.PreviewTitle>}
    {headerContent}
    {children}
    {footerContent}
  </S.Preview>
);

const FilePreview: FC<FilePreviewProps> = ({
  title,
  file,
  headerContent,
  footerContent,
  width,
  height,
  variant = 'default',
}) => {
  const isA4 = variant === 'a4';
  // In 'viewer' mode images are rendered by FileViewer (like PDFs) so they get
  // the same centered frame and Zoom/Download panel.
  const useViewerForImage = variant === 'viewer';
  const previewKind = getFilePreviewKind(file?.mimeType);
  const isTypePDF = previewKind === 'pdf';
  // The `<img src>`: the JPEG previewUrl for a ready HEIC preview, the original
  // url for legacy files, or undefined when a preview is pending/failed.
  const previewSrc = resolvePreviewSrc(file);
  // Browser-renderable images (png/jpg/webp/...) are shown inline, and so is a
  // ready HEIC→JPEG preview regardless of the original (non-renderable) MIME.
  // tiff/heic without a ready preview fall back to a download-only placeholder.
  const isTypeImage =
    (previewKind === 'image' && isRenderableImageMimeType(file?.mimeType)) ||
    (isPreviewReady(file) && !!previewSrc);

  // Fall back to the placeholder if the resolved image fails to load.
  const [previewError, setPreviewError] = useState(false);
  useEffect(() => setPreviewError(false), [previewSrc]);

  const showImage = isTypeImage && !!previewSrc && !previewError;
  const showFallback = !isTypePDF && !showImage;

  const fileName =
    (file as { fileName?: string; name?: string } | null)?.fileName ||
    (file as { fileName?: string; name?: string } | null)?.name ||
    'document';

  if (!file) {
    return (
      <FilePreviewWrapper title={title} headerContent={headerContent} footerContent={footerContent}>
        <S.PreviewContent width={width} height={height} $variant={variant} />
      </FilePreviewWrapper>
    );
  }

  // A HEIC→JPEG preview is still being generated: show a spinner rather than
  // the raw HEIC (which the browser can't render) or a "can't preview" state.
  if (isPreviewPending(file)) {
    return (
      <FilePreviewWrapper title={title} headerContent={headerContent} footerContent={footerContent}>
        <S.PreviewContent width={width} height={height} $variant={variant}>
          <Spin />
        </S.PreviewContent>
      </FilePreviewWrapper>
    );
  }

  // A4 variant: PDFs and images share one FileViewer so they render in an
  // identical A4 frame with the same Zoom/Download panel.
  if (isA4 && (isTypePDF || isTypeImage)) {
    return (
      <FilePreviewWrapper title={title} headerContent={headerContent} footerContent={footerContent}>
        <S.PreviewContent $variant={variant}>
          <FileViewer selectedFile={file} width="100%" height="100%" fitToWidth />
        </S.PreviewContent>
      </FilePreviewWrapper>
    );
  }

  return (
    <FilePreviewWrapper title={title} headerContent={headerContent} footerContent={footerContent}>
      <S.PreviewContent width={width} height={height} $variant={variant}>
        {(isTypePDF || (showImage && useViewerForImage)) && (
          <FileViewer
            selectedFile={file}
            width={width || '437px'}
            height={height || '622px'}
            pageWidth={542}
          />
        )}

        {showImage && !useViewerForImage && (
          <Image
            src={previewSrc}
            alt={fileName}
            wrapperClassName="file-preview-image"
            preview={{
              mask: 'Click to zoom',
            }}
            onError={() => setPreviewError(true)}
          />
        )}

        {showFallback && (
          <S.Empty>
            <S.EmptyIcon>
              <IconEyeOff width={32} height={32} />
            </S.EmptyIcon>

            <S.EmptyTitle>Preview can't be displayed</S.EmptyTitle>

            <S.EmptyDescription>
              This file can't be previewed in the browser. Download it to view.
            </S.EmptyDescription>

            {file?.url && (
              <Button
                type="primary"
                size="large"
                icon={<IconDownload />}
                onClick={() => downloadFile(file.url, fileName, true)}
              >
                Download document file
              </Button>
            )}
          </S.Empty>
        )}
      </S.PreviewContent>
    </FilePreviewWrapper>
  );
};

export default FilePreview;
